import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import Input from "../../ui/form/Input";
import CustomSelect from "../../ui/form/CustomSelect";
import { ModalBody, ModalFooter } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  createCity,
  fetchAllCities,
  fetchAllCountry,
  fetchAllStates,
} from "../../redux/slice/usersSlice";
import { useToast } from "../../helpers/contexts/ToastContext";
import { handleApiError } from "../../utility/handleApiError";
import withModal from "../withModal";
import { Formik } from "formik";
import Btn from "../../ui/button/Btn";

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required("City name is required"),
  country_id: Yup.object().nullable().required("Country is required"),
  state_id: Yup.object().nullable().required("State is required"),
});

const normalizeOptions = (list = []) =>
  list.map((i) => ({
    label: String(i.name || ""),
    value: i.id,
  }));

const QuickCityModalInner = ({ onClose, onSave, initialCityName = "" }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const debounceRef = useRef();

  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);
  const [stateLoading, setStateLoading] = useState(false);

  const initialValues = useMemo(
    () => ({
      name: initialCityName,
      country_id: null,
      state_id: null,
    }),
    [initialCityName]
  );

  // Load countries
  const loadCountries = useCallback(
    async (searchText = "") => {
      try {
        setCountryLoading(true);
        const result = await dispatch(
          fetchAllCountry({
            page: 1,
            limit: 10,
            is_active: true,
            ...(searchText ? { search: searchText } : {}),
          })
        ).unwrap();
        setCountryOptions(normalizeOptions(result?.Countries || result?.data || []));
      } catch (err) {
        console.error("Failed to fetch countries", err);
        setCountryOptions([]);
      } finally {
        setCountryLoading(false);
      }
    },
    [dispatch]
  );

  // Load states filtered by country
  const loadStates = useCallback(
    async (countryId, searchText = "") => {
      if (!countryId) {
        setStateOptions([]);
        return;
      }
      try {
        setStateLoading(true);
        const result = await dispatch(
          fetchAllStates({
            page: 1,
            limit: 10,
            is_active: true,
            country_id: countryId,
            ...(searchText ? { search: searchText } : {}),
          })
        ).unwrap();
        setStateOptions(normalizeOptions(result?.States || result?.data || []));
      } catch (err) {
        console.error("Failed to fetch states", err);
        setStateOptions([]);
      } finally {
        setStateLoading(false);
      }
    },
    [dispatch]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleCountrySearch = useCallback(
    (inputValue, meta) => {
      if (!meta || (meta.action !== "input-change" && meta.action !== "menu-open" && meta.action !== "set-value")) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (meta && (meta.action === "menu-open" || meta.action === "input-change" || meta.action === "set-value")) {
        setCountryOptions([]);
      }

      debounceRef.current = setTimeout(() => {
        loadCountries(String(inputValue || ""));
      }, 300);
    },
    [loadCountries]
  );

  const handleStateSearch = useCallback(
    (countryId) => (inputValue, meta) => {
      if (!meta || (meta.action !== "input-change" && meta.action !== "menu-open" && meta.action !== "set-value")) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (meta && (meta.action === "menu-open" || meta.action === "input-change" || meta.action === "set-value")) {
        setStateOptions([]);
      }

      debounceRef.current = setTimeout(() => {
        loadStates(countryId, String(inputValue || ""));
      }, 300);
    },
    [loadStates]
  );

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      const payload = {
        country_name: values.country_id.label,
        state_name: values.state_id.label,
        cities: [values.name.trim()],
      };

      const result = await dispatch(createCity(payload)).unwrap();
      const responseData = result?.data;
      let createdCity = Array.isArray(responseData)
        ? responseData[0]
        : responseData?.Cities?.[0] ||
          responseData?.cities?.[0] ||
          responseData?.city ||
          responseData;

      try {
        const citiesResult = await dispatch(
          fetchAllCities({
            country_id: values.country_id.value,
            state_id: values.state_id.value,
            search: values.name.trim(),
            page: 1,
            limit: 10,
          })
        ).unwrap();
        const cityList = Array.isArray(citiesResult)
          ? citiesResult
          : citiesResult?.Cities || citiesResult?.data || [];
        const matchedCity = cityList.find(
          (city) => {
            const returnedName = String(city.name || "").trim().toLowerCase();
            const searchName = values.name.trim().toLowerCase();
            return returnedName === searchName || returnedName.startsWith(searchName + ",");
          }
        );
        createdCity = matchedCity || createdCity;
      } catch (lookupError) {
        console.error("City created, but failed to retrieve it", lookupError);
      }

      showToast(result?.message || "City created successfully", "success");
      onSave?.(createdCity || { name: values.name.trim() });
      onClose();
    } catch (err) {
      handleApiError(err, { setErrors, showToast });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      key={initialCityName}
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        setFieldValue,
        handleChange,
        handleBlur,
        touched,
        errors,
        isSubmitting,
        handleSubmit: formikSubmit,
      }) => (
        <>
          <ModalBody>
            <div className="grid columns-1 column-gap-10 row-gap-8">
              <Input
                label={{ name: "City Name" }}
                input={{
                  name: "name",
                  placeholder: "Enter City Name",
                  value: values.name,
                  onChange: handleChange,
                  onBlur: handleBlur,
                }}
                required={true}
                validation={{
                  isInvalid: touched.name && !!errors.name,
                  message: errors.name,
                }}
              />

              <CustomSelect
                label={{ name: "Country" }}
                selectProps={{ placeholder: "Select Country" }}
                onInputChange={handleCountrySearch}
                onMenuOpen={() => handleCountrySearch("", { action: "menu-open" })}
                options={countryOptions}
                value={values.country_id}
                isSearchable
                isClearable
                isLoading={countryLoading}
                required={true}
                onChange={(opt) => {
                  setFieldValue("country_id", opt);
                  // Reset state when country changes
                  setFieldValue("state_id", null);
                  setStateOptions([]);
                  if (opt?.value) {
                    loadStates(opt.value);
                  }
                }}
                validation={{
                  isInvalid: touched.country_id && !!errors.country_id,
                  message: errors.country_id,
                }}
              />

              <CustomSelect
                label={{ name: "State" }}
                selectProps={{ placeholder: "Select State" }}
                onInputChange={handleStateSearch(values.country_id?.value)}
                onMenuOpen={() =>
                  handleStateSearch(values.country_id?.value)("", { action: "menu-open" })
                }
                options={stateOptions}
                value={values.state_id}
                isSearchable
                isClearable
                isLoading={stateLoading}
                isDisabled={!values.country_id}
                required={true}
                onChange={(opt) => setFieldValue("state_id", opt)}
                validation={{
                  isInvalid: touched.state_id && !!errors.state_id,
                  message: errors.state_id,
                }}
              />
            </div>
          </ModalBody>
          <ModalFooter className="gap-8">
            <Btn
              variant="cancel"
              title="Cancel"
              onClick={onClose}
              size="modal"
              disabled={isSubmitting}
            />
            <Btn
              type="button"
              variant="primary"
              title="Create City"
              size="modal"
              loading={isSubmitting}
              onClick={formikSubmit}
            />
          </ModalFooter>
        </>
      )}
    </Formik>
  );
};

const QuickCityModal = withModal(QuickCityModalInner);
export default QuickCityModal;
